import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';

import sheetUrl from '../assets/fx/skill-fx.webp?url';

/**
 * The spell-effect atlas: one texture, many sprites, addressed by name.
 *
 * ── One texture, not twenty-three files ──────────────────────────────────────
 *
 * The item and skill icons are individual WebPs because the client has to know *which* of
 * them exist — art arrives one sheet at a time and a missing icon falls back to a glyph.
 * None of that applies here: every sprite ships together, nothing falls back, and the
 * consumer is a material rather than an `<img>`. So this stays a single 446 KB atlas and
 * every effect is a sub-rectangle of it — **one texture bind for the whole spell system**,
 * where twenty-three files would be twenty-three loads and twenty-three binds.
 *
 * ── Why the alpha is the luminance ───────────────────────────────────────────
 *
 * The sheet is drawn on pure black for additive blending, where black contributes nothing
 * and disappears for free. Baking `alpha = max(r,g,b)` at conversion time keeps that
 * property *and* makes the same texture work on a material that is not additive — a
 * brightness is exactly how much of a glowing sprite should reach the screen. No keying, no
 * threshold, and no fringe around a soft edge, which a colour key would have left on every
 * one of these.
 *
 * ── The rectangles are measured, not a grid ──────────────────────────────────
 *
 * The prompt asked for an 8 × 8 grid and the model did not draw one: the rows have different
 * heights and the sprites different widths, so slicing on a uniform grid cuts a fireball in
 * half. These came from finding the bright regions and reading their bounding boxes, then
 * checking every one against a labelled overlay — two were wrong on that pass and would have
 * been silent, because a rectangle that contains the wrong art still renders something.
 */

/** Pixel rectangles into the atlas: `[x, y, width, height]`, top-left origin. */
const SPRITES = {
  lightning_bolt: [391, 5, 79, 141],
  lightning_bolt_alt: [211, 164, 89, 150],
  lightning_bolt_thin: [392, 170, 74, 143],
  lightning_forked: [23, 170, 127, 143],
  lightning_arc: [196, 12, 122, 132],
  lightning_arc_ring: [17, 25, 138, 108],
  lightning_burst: [530, 10, 136, 137],
  lightning_burst_sm: [704, 15, 129, 131],
  lightning_orb: [867, 7, 143, 144],
  lightning_orb_big: [525, 169, 148, 145],
  lightning_chain: [686, 200, 338, 85],
  fire_comet: [27, 341, 163, 148],
  fire_ring: [300, 339, 204, 159],
  fire_embers: [545, 345, 246, 148],
  fire_magma_pool: [816, 355, 194, 120],
  ice_crystals: [18, 531, 206, 218],
  ice_ring: [274, 549, 241, 177],
  ice_snowflakes: [561, 557, 184, 172],
  ice_shard: [810, 552, 194, 172],
  earth_cracks: [14, 812, 248, 175],
  earth_debris: [295, 785, 181, 226],
  void_portal: [519, 788, 246, 218],
  void_swirl: [800, 800, 210, 196],
} as const;

export type FxSprite = keyof typeof SPRITES;

/** Every sprite name, for tests and for anything that wants to enumerate them. */
export const FX_SPRITES = Object.keys(SPRITES) as FxSprite[];

const SHEET_PX = 1024;

/**
 * The UV window for one sprite, as Babylon's texture offsets.
 *
 * `vOffset` is measured from the **bottom**, because texture space runs upward while the
 * atlas was authored downward. Getting that wrong is the classic atlas bug: every sprite
 * renders, each one is simply the wrong effect, mirrored about the middle of the sheet.
 */
export function spriteUv(name: FxSprite): {
  uOffset: number;
  vOffset: number;
  uScale: number;
  vScale: number;
} {
  const [x, y, w, h] = SPRITES[name];
  return {
    uOffset: x / SHEET_PX,
    vOffset: 1 - (y + h) / SHEET_PX,
    uScale: w / SHEET_PX,
    vScale: h / SHEET_PX,
  };
}

/**
 * A texture windowed onto one sprite.
 *
 * **A new `Texture` per call, sharing one decoded image.** Babylon caches by URL, so the
 * bytes are fetched and decoded once however many of these exist; what each instance owns is
 * its own UV window, which is exactly what has to differ per effect. Sharing a single
 * `Texture` and mutating its offsets would make the last caster's spell repaint everybody
 * else's.
 *
 * `hasAlpha` because the sheet carries one, and `ALPHA_ADD` on the material is what makes
 * the black disappear rather than sitting as a square over the floor.
 */
export function spriteTexture(scene: Scene, name: FxSprite): Texture {
  const tex = new Texture(sheetUrl, scene, true, false, Texture.BILINEAR_SAMPLINGMODE);
  const uv = spriteUv(name);
  tex.uOffset = uv.uOffset;
  tex.vOffset = uv.vOffset;
  tex.uScale = uv.uScale;
  tex.vScale = uv.vScale;
  // Clamped, or the bilinear filter samples the neighbouring sprite along every edge and
  // a fireball arrives with a sliver of lightning down one side.
  tex.wrapU = Texture.CLAMP_ADDRESSMODE;
  tex.wrapV = Texture.CLAMP_ADDRESSMODE;
  tex.hasAlpha = true;
  return tex;
}

/**
 * Which sprite a cast draws, from the skill's own catalogue entry.
 *
 * ── Derived from the colour, deliberately ────────────────────────────────────
 *
 * A `visual_assets.fx_id` per skill would be a hundred more strings to author and a hundred
 * chances to forget one — and unlike `range`, nothing about the *design* differs between two
 * fire skills' particles. What already distinguishes them is the palette the catalogue gives
 * every skill for its bolt, so the element is read from that: warm is fire, cold blue is
 * frost, cyan-white is lightning, purple is void, brown is earth.
 *
 * **The reach for a per-skill override is `range`'s**: add a field when a skill genuinely
 * needs one, rather than pre-emptively for all hundred. This is the same call `animation_clip`
 * makes with its cast styles.
 */
export type FxElement = 'fire' | 'frost' | 'lightning' | 'void' | 'earth';

/** Ground footprints, projectile heads and impacts, per element. */
const BY_ELEMENT: Record<FxElement, { ground: FxSprite; bolt: FxSprite; impact: FxSprite }> = {
  fire: { ground: 'fire_ring', bolt: 'fire_comet', impact: 'fire_embers' },
  frost: { ground: 'ice_ring', bolt: 'ice_shard', impact: 'ice_snowflakes' },
  lightning: { ground: 'lightning_arc_ring', bolt: 'lightning_bolt', impact: 'lightning_burst' },
  void: { ground: 'void_portal', bolt: 'lightning_orb', impact: 'void_swirl' },
  earth: { ground: 'earth_cracks', bolt: 'ice_shard', impact: 'earth_debris' },
};

/**
 * The element a colour reads as.
 *
 * Hue and value, not a lookup of exact hex values: the catalogue's palette is authored per
 * skill and two fire spells are different oranges. A table of literals would match neither.
 */
export function elementForColour(hex: string | null | undefined): FxElement {
  const rgb = parseRgb(hex);
  if (!rgb) return 'lightning';
  const [r, g, b] = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  // **Near-black is void, not earth.** `Singularity` is `#000000`, which has no dominant
  // channel and would otherwise fall into the grey bucket and draw cracked ground.
  if (max < 0.12) return 'void';
  // Near-grey with no dominant channel: steel and smoke, which read as earth.
  if (max - min < 0.12) return 'earth';
  // **Green has no family on the sheet, and maps to earth rather than frost.**
  //
  // The atlas has five: lightning, fire, ice, earth and void. The catalogue's palette has a
  // green — poison, nature, the ranger skills — and green is blue-adjacent enough that the
  // cold branch below swallowed it, so `Precision Shot` and `Vault` were drawing ice rings.
  // Earth is the least wrong of the five for something organic; the honest fix is a green
  // family on the next sheet, and this is a note that it is missing rather than a mapping
  // anybody should be pleased with.
  if (g > r && g > b) return 'earth';
  if (r >= g && r >= b) {
    // Warm. Brown is earth — a low-value red with green under it — and orange is fire.
    return max < 0.45 && g > b ? 'earth' : 'fire';
  }
  if (b >= r && b >= g) {
    // Cold. Purple has red *above* green where cyan and pale blue do not.
    if (r > g) return 'void';
    // **Red is what separates cyan from pale ice**, not green — both are green-heavy.
    // Electric cyan is `#00f3ff`, almost no red; frost is `#a6f0ff`, a pale blue with
    // plenty of it. Discriminating on green put every ice colour in the lightning bucket.
    return r < 0.35 && g > 0.6 ? 'lightning' : 'frost';
  }
  return 'frost';
}

/** The three sprites a skill uses, from its catalogue colour. */
export function fxFor(colour: string | null | undefined) {
  return BY_ELEMENT[elementForColour(colour)];
}

function parseRgb(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}
